import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mainSiteUrl } from "@/mocks/categories";

const cards = [
  {
    id: "spreadsheet",
    icon: "ri-table-line",
    titleKey: "featured.spreadsheet.title",
    descKey: "featured.spreadsheet.desc",
    image:
      "https://readdy.ai/api/search-image?query=A%20laptop%20screen%20showing%20a%20clean%20product%20spreadsheet%20table%20with%20columns%20and%20colorful%20rows%20on%20a%20bright%20modern%20desk%2C%20soft%20daylight%2C%20editorial%20tech%20workspace%20photography%2C%20crisp%20organized%20mood%2C%20high%20detail&width=800&height=600&seq=feat-spreadsheet-01&orientation=landscape",
    internal: true,
  },
  {
    id: "review",
    icon: "ri-star-line",
    titleKey: "featured.review.title",
    descKey: "featured.review.desc",
    image:
      "https://readdy.ai/api/search-image?query=A%20set%20of%20glowing%20five%20star%20ratings%20beside%20a%20magnifying%20glass%20and%20a%20notebook%20on%20a%20soft%20light%20background%2C%20warm%20natural%20light%2C%20editorial%20review%20concept%20photography%2C%20trustworthy%20and%20modern%20mood%2C%20high%20detail&width=800&height=600&seq=feat-review-02&orientation=landscape",
    internal: false,
  },
  {
    id: "shipping",
    icon: "ri-ship-line",
    titleKey: "featured.shipping.title",
    descKey: "featured.shipping.desc",
    image:
      "https://readdy.ai/api/search-image?query=A%20neat%20line%20of%20cardboard%20parcels%20of%20different%20sizes%20with%20a%20shipping%20label%20in%20focus%20on%20a%20light%20background%2C%20soft%20studio%20light%2C%20editorial%20logistics%20photography%2C%20fresh%20and%20efficient%20mood%2C%20high%20detail&width=800&height=600&seq=feat-shipping-03&orientation=landscape",
    internal: false,
  },
  {
    id: "qc",
    icon: "ri-search-eye-line",
    titleKey: "featured.qc.title",
    descKey: "featured.qc.desc",
    image:
      "https://readdy.ai/api/search-image?query=A%20gloved%20hand%20inspecting%20a%20white%20sneaker%20with%20a%20magnifying%20glass%20over%20a%20bright%20inspection%20table%2C%20soft%20light%2C%20editorial%20quality%20control%20photography%2C%20precise%20and%20clean%20mood%2C%20high%20detail&width=800&height=600&seq=feat-qc-04&orientation=landscape",
    internal: false,
  },
  {
    id: "warehouse",
    icon: "ri-archive-line",
    titleKey: "featured.warehouse.title",
    descKey: "featured.warehouse.desc",
    image:
      "https://readdy.ai/api/search-image?query=Orderly%20warehouse%20shelving%20with%20labeled%20storage%20boxes%20in%20warm%20ambient%20light%2C%20soft%20depth%2C%20editorial%20logistics%20photography%2C%20organized%20and%20calm%20mood%2C%20high%20detail&width=800&height=600&seq=feat-warehouse-05&orientation=landscape",
    internal: false,
  },
  {
    id: "safety",
    icon: "ri-shield-check-line",
    titleKey: "featured.safety.title",
    descKey: "featured.safety.desc",
    image:
      "https://readdy.ai/api/search-image?query=A%20soft%20lit%20padlock%20and%20shield%20symbol%20resting%20on%20a%20clean%20surface%20with%20subtle%20bokeh%20background%2C%20editorial%20security%20photography%2C%20calm%20protective%20mood%2C%20high%20detail&width=800&height=600&seq=feat-safety-06&orientation=landscape",
    internal: false,
  },
];

const searches = [
  "searches.item1",
  "searches.item2",
  "searches.item3",
  "searches.item4",
  "searches.item5",
  "searches.item6",
];

export default function FeaturedSection() {
  const { t } = useTranslation();

  return (
    <section id="spreadsheet" className="bg-background-100/60">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
            {t("featured.title")}
          </h2>
          <p className="mt-3 text-foreground-600 leading-relaxed">
            {t("featured.subtitle")}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card) => {
            const inner = (
              <>
                <div className="relative w-full h-40 overflow-hidden rounded-t-lg">
                  <img
                    src={card.image}
                    alt={`${t(card.titleKey)} — AllChinaBuy Spreadsheet`}
                    title={`${t(card.titleKey)} AllChinaBuy Spreadsheet`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
                <div className="flex flex-col p-6">
                  <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={card.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                    {t(card.titleKey)}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    {t(card.descKey)}
                  </p>
                </div>
              </>
            );

            const className =
              "group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer";

            if (card.internal) {
              return (
                <Link
                  key={card.id}
                  to="/article/allchinabuy-spreadsheet-guide"
                  className={className}
                >
                  {inner}
                </Link>
              );
            }

            return (
              <a
                key={card.id}
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className={className}
              >
                {inner}
              </a>
            );
          })}
        </div>

        <div className="mt-12">
          <h3 className="font-heading text-lg font-semibold text-foreground-950">
            {t("searches.title")}
          </h3>
          <p className="mt-1 text-sm text-foreground-600">{t("searches.subtitle")}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {searches.map((key) => (
              <a
                key={key}
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 rounded-full bg-background-50 border border-background-200 px-4 py-2 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 whitespace-nowrap cursor-pointer"
              >
                {t(key)}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}